import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Upload,
  Folder,
  FolderPlus,
  FolderOpen,
  Image as ImageIcon,
  Trash2,
  Copy,
  CheckCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  Home,
  Grid,
  List,
  Search,
  Download,
  Eye,
  MoreVertical,
  Link,
  Pencil,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for media files
const getFullUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it starts with /api, prepend the backend URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

export default function MediaManager() {
  const { isDarkMode } = useTheme();
  
  // Theme-aware classes
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const bgHover = isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  // State
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(['platform-images']);
  const [currentPath, setCurrentPath] = useState('platform-images');
  const [loading, setLoading] = useState(true);
  const [loadingTree, setLoadingTree] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Rename state
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameFile, setRenameFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Build folder tree structure
  const buildFolderTree = useCallback((flatFolders, basePath = 'platform-images') => {
    const tree = [{
      name: 'platform-images',
      path: 'platform-images',
      children: [],
      isRoot: true
    }];
    
    // Add folders from current view to tree
    flatFolders.forEach(folder => {
      const pathParts = folder.path.split('/').filter(Boolean);
      if (pathParts.length === 2 && pathParts[0] === 'platform-images') {
        tree[0].children.push({
          name: folder.name,
          path: folder.path.replace(/\/$/, ''),
          children: [],
          count: folder.count || 0
        });
      }
    });
    
    return tree;
  }, []);

  // Fetch folder tree
  const fetchFolderTree = useCallback(async () => {
    setLoadingTree(true);
    try {
      const response = await axios.get(`${API}/admin/media`, {
        params: { path: 'platform-images' }
      });
      const tree = buildFolderTree(response.data.folders || []);
      setFolderTree(tree);
    } catch (error) {
      console.error('Failed to fetch folder tree:', error);
      setFolderTree([{ name: 'platform-images', path: 'platform-images', children: [], isRoot: true }]);
    } finally {
      setLoadingTree(false);
    }
  }, [buildFolderTree]);

  // Fetch files and folders for current path
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/media`, {
        params: { path: currentPath }
      });
      setFiles(response.data.files || []);
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      setFiles([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPath]);

  useEffect(() => {
    fetchFolderTree();
  }, [fetchFolderTree]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Toggle folder expansion in tree
  const toggleFolderExpand = (folderPath) => {
    setExpandedFolders(prev => 
      prev.includes(folderPath) 
        ? prev.filter(p => p !== folderPath)
        : [...prev, folderPath]
    );
  };

  // Handle file upload
  const handleUpload = async (uploadFiles) => {
    if (!uploadFiles.length) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    for (const file of uploadFiles) {
      formData.append('files', file);
    }
    
    try {
      await axios.post(`${API}/admin/media/upload?path=${encodeURIComponent(currentPath)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success(`Uploaded ${uploadFiles.length} file(s) successfully`);
      fetchMedia();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload files';
      toast.error(msg);
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleUpload(droppedFiles);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleUpload(selectedFiles);
    e.target.value = '';
  };

  // Create new folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setCreatingFolder(true);
    try {
      await axios.post(`${API}/admin/media/folder`, {
        path: currentPath,
        name: newFolderName.trim()
      });
      toast.success(`Folder "${newFolderName}" created`);
      setNewFolderName('');
      setNewFolderOpen(false);
      fetchMedia();
      fetchFolderTree();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create folder';
      toast.error(msg);
      console.error(error);
    } finally {
      setCreatingFolder(false);
    }
  };

  // Delete file
  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/media`, {
        params: { path: file.path }
      });
      toast.success('File deleted');
      fetchMedia();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete file';
      toast.error(msg);
      console.error(error);
    }
  };

  // Rename file
  const handleRename = async () => {
    if (!newFileName.trim() || !renameFile) return;
    
    setRenaming(true);
    try {
      await axios.put(`${API}/admin/media/rename`, {
        old_path: renameFile.path,
        new_name: newFileName.trim()
      });
      toast.success('File renamed successfully');
      setRenameOpen(false);
      setRenameFile(null);
      setNewFileName('');
      fetchMedia();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to rename file';
      toast.error(msg);
      console.error(error);
    } finally {
      setRenaming(false);
    }
  };

  // Open rename dialog
  const openRenameDialog = (file) => {
    setRenameFile(file);
    // Extract filename without extension for editing
    const nameParts = file.name.split('.');
    const ext = nameParts.length > 1 ? nameParts.pop() : '';
    const nameWithoutExt = nameParts.join('.');
    setNewFileName(nameWithoutExt);
    setRenameOpen(true);
  };

  // Copy URL to clipboard
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Navigate to folder
  const navigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
    if (!expandedFolders.includes(folderPath)) {
      setExpandedFolders(prev => [...prev, folderPath]);
    }
  };

  // Get breadcrumb parts
  const breadcrumbs = currentPath.split('/').filter(Boolean);

  // Filter files by search
  const filteredFiles = files.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredFolders = folders.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Render folder tree item
  const renderTreeItem = (folder, depth = 0) => {
    const isExpanded = expandedFolders.includes(folder.path);
    const isSelected = currentPath === folder.path;
    const hasChildren = folder.children && folder.children.length > 0;
    
    return (
      <div key={folder.path}>
        <div
          data-testid={`folder-tree-item-${folder.name}`}
          className={`
            flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors
            ${isSelected ? 'bg-[#6D4AFF]/20 text-[#6D4AFF]' : `${textSecondary} ${bgHover}`}
          `}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          onClick={() => navigateToFolder(folder.path)}
        >
          {hasChildren || folder.isRoot ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleFolderExpand(folder.path); }}
              className="p-0.5 hover:bg-black/10 rounded"
            >
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          {isExpanded ? (
            <FolderOpen className="w-4 h-4 text-amber-400 flex-shrink-0" />
          ) : (
            <Folder className="w-4 h-4 text-amber-400 flex-shrink-0" />
          )}
          <span className="text-sm truncate flex-1">{folder.name}</span>
          {folder.count > 0 && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {folder.count}
            </Badge>
          )}
        </div>
        {isExpanded && hasChildren && (
          <div>
            {folder.children.map(child => renderTreeItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
      {/* Left Sidebar - Folder Tree */}
      <div className={`w-64 flex-shrink-0 ${bgSecondary} rounded-lg border ${borderColor} flex flex-col`}>
        <div className={`p-3 border-b ${borderColor} flex items-center justify-between`}>
          <h3 className={`font-medium ${textPrimary} text-sm`}>Folders</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => { fetchFolderTree(); fetchMedia(); }}
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${textMuted}`} />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loadingTree ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-[#6D4AFF] animate-spin" />
            </div>
          ) : (
            <div className="space-y-0.5">
              {folderTree.map(folder => renderTreeItem(folder))}
            </div>
          )}
        </div>
        <div className={`p-2 border-t ${borderColor}`}>
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${borderColor} ${textSecondary}`}
            onClick={() => setNewFolderOpen(true)}
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
        </div>
      </div>

      {/* Right Content - Files */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className={`flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between p-3 ${bgSecondary} rounded-lg border ${borderColor} mb-3`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Breadcrumbs */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPath('platform-images')}
              className={`h-7 px-2 ${textMuted}`}
            >
              <Home className="w-3.5 h-3.5" />
            </Button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                <ChevronRight className={`w-3.5 h-3.5 ${textMuted}`} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPath(breadcrumbs.slice(0, index + 1).join('/'))}
                  className={`h-7 px-2 ${index === breadcrumbs.length - 1 ? textPrimary : textMuted}`}
                >
                  {crumb}
                </Button>
              </React.Fragment>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${textMuted}`} />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-8 h-8 w-40 text-sm ${bgTertiary} ${borderColor} ${textPrimary}`}
              />
            </div>

            {/* View Mode Toggle */}
            <div className={`flex items-center rounded-md ${bgTertiary} p-0.5`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`h-7 w-7 p-0 ${viewMode === 'grid' ? 'bg-[#6D4AFF] text-white' : textMuted}`}
              >
                <Grid className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={`h-7 w-7 p-0 ${viewMode === 'list' ? 'bg-[#6D4AFF] text-white' : textMuted}`}
              >
                <List className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Upload Button */}
            <label>
              <Button
                size="sm"
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9] cursor-pointer h-8"
                asChild
              >
                <span>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload
                </span>
              </Button>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
                data-testid="file-upload-input"
              />
            </label>
          </div>
        </div>

        {/* Drop Zone / Content Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative flex-1 rounded-lg border-2 border-dashed transition-colors overflow-hidden
            ${isDragging 
              ? 'border-[#6D4AFF] bg-[#6D4AFF]/10' 
              : `${borderColor} ${bgSecondary}`
            }
          `}
        >
          {/* Upload Progress */}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg z-10">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-[#6D4AFF] animate-spin mx-auto mb-3" />
                <p className="text-white font-medium">Uploading... {uploadProgress}%</p>
                <div className="w-40 h-1.5 bg-gray-700 rounded-full mt-2 mx-auto overflow-hidden">
                  <div 
                    className="h-full bg-[#6D4AFF] transition-all" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : (
            <div className="h-full overflow-y-auto p-4">
              {/* Empty State */}
              {filteredFolders.length === 0 && filteredFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className={`p-4 rounded-full ${bgTertiary} mb-4`}>
                    <ImageIcon className={`w-10 h-10 ${textMuted}`} />
                  </div>
                  <p className={`${textPrimary} font-medium mb-1`}>No files yet</p>
                  <p className={`text-sm ${textMuted} mb-4`}>
                    Drag and drop files here or click Upload
                  </p>
                  <label>
                    <Button className="bg-[#6D4AFF] hover:bg-[#5B3FD9] cursor-pointer" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Files
                      </span>
                    </Button>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <>
                  {/* Folders in current view */}
                  {filteredFolders.length > 0 && (
                    <div className="mb-4">
                      <p className={`text-xs font-medium ${textMuted} mb-2 uppercase tracking-wide`}>Folders</p>
                      <div className={viewMode === 'grid' 
                        ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
                        : "space-y-2"
                      }>
                        {filteredFolders.map((folder) => (
                          viewMode === 'grid' ? (
                            <Card
                              key={folder.name}
                              data-testid={`folder-card-${folder.name}`}
                              className={`${bgTertiary} ${borderColor} cursor-pointer hover:border-[#6D4AFF]/50 transition-colors`}
                              onClick={() => navigateToFolder(folder.path.replace(/\/$/, ''))}
                            >
                              <CardContent className="p-3 text-center">
                                <Folder className="w-10 h-10 mx-auto mb-2 text-amber-400" />
                                <p className={`text-sm ${textPrimary} truncate`}>{folder.name}</p>
                                <p className={`text-xs ${textMuted}`}>{folder.count || 0} items</p>
                              </CardContent>
                            </Card>
                          ) : (
                            <div
                              key={folder.name}
                              data-testid={`folder-row-${folder.name}`}
                              onClick={() => navigateToFolder(folder.path.replace(/\/$/, ''))}
                              className={`flex items-center gap-3 p-2.5 ${bgTertiary} rounded-lg cursor-pointer hover:border-[#6D4AFF]/50 border ${borderColor} transition-colors`}
                            >
                              <Folder className="w-8 h-8 text-amber-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={`${textPrimary} truncate`}>{folder.name}</p>
                                <p className={`text-sm ${textMuted}`}>{folder.count || 0} items</p>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {filteredFiles.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium ${textMuted} mb-2 uppercase tracking-wide`}>Files</p>
                      {viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {filteredFiles.map((file) => (
                            <Card
                              key={file.name}
                              data-testid={`file-card-${file.name}`}
                              className={`${bgTertiary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors group relative`}
                            >
                              <CardContent className="p-2">
                                <div 
                                  className="aspect-square rounded overflow-hidden mb-2 bg-black/20 cursor-pointer"
                                  onClick={() => { setSelectedFile(file); setPreviewOpen(true); }}
                                >
                                  <img 
                                    src={getFullUrl(file.url)} 
                                    alt={file.name}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                                    onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                                  />
                                </div>
                                <p className={`text-xs ${textPrimary} truncate`} title={file.name}>{file.name}</p>
                                <p className={`text-xs ${textMuted}`}>{formatSize(file.size)}</p>
                                
                                {/* 3-Dot Menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      data-testid={`file-menu-${file.name}`}
                                      className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 ${bgSecondary} shadow-md`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className={`${bgSecondary} ${borderColor}`} align="end">
                                    <DropdownMenuItem
                                      className={`${textSecondary} cursor-pointer`}
                                      onClick={() => openRenameDialog(file)}
                                    >
                                      <Pencil className="w-4 h-4 mr-2" />
                                      Edit Name
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className={`${textSecondary} cursor-pointer`}
                                      onClick={() => copyUrl(getFullUrl(file.url))}
                                    >
                                      <Link className="w-4 h-4 mr-2" />
                                      Get Image Link
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className={`${textSecondary} cursor-pointer`}
                                      onClick={() => { setSelectedFile(file); setPreviewOpen(true); }}
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      Preview
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className={borderColor} />
                                    <DropdownMenuItem
                                      className="text-red-400 cursor-pointer"
                                      onClick={() => handleDelete(file)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredFiles.map((file) => (
                            <div
                              key={file.name}
                              data-testid={`file-row-${file.name}`}
                              className={`flex items-center gap-3 p-2.5 ${bgTertiary} rounded-lg border ${borderColor} group`}
                            >
                              <div 
                                className="w-12 h-12 rounded overflow-hidden bg-black/20 flex-shrink-0 cursor-pointer"
                                onClick={() => { setSelectedFile(file); setPreviewOpen(true); }}
                              >
                                <img 
                                  src={getFullUrl(file.url)} 
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`${textPrimary} truncate`}>{file.name}</p>
                                <p className={`text-sm ${textMuted}`}>{formatSize(file.size)}</p>
                              </div>
                              
                              {/* 3-Dot Menu for List View */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`file-menu-list-${file.name}`}
                                    className={`h-8 w-8 ${textMuted}`}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className={`${bgSecondary} ${borderColor}`} align="end">
                                  <DropdownMenuItem
                                    className={`${textSecondary} cursor-pointer`}
                                    onClick={() => openRenameDialog(file)}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit Name
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className={`${textSecondary} cursor-pointer`}
                                    onClick={() => copyUrl(getFullUrl(file.url))}
                                  >
                                    <Link className="w-4 h-4 mr-2" />
                                    Get Image Link
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className={`${textSecondary} cursor-pointer`}
                                    onClick={() => { setSelectedFile(file); setPreviewOpen(true); }}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Preview
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className={borderColor} />
                                  <DropdownMenuItem
                                    className="text-red-400 cursor-pointer"
                                    onClick={() => handleDelete(file)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Drag Overlay */}
          {isDragging && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#6D4AFF]/20 rounded-lg border-2 border-[#6D4AFF] pointer-events-none">
              <div className="text-center">
                <Upload className="w-10 h-10 text-[#6D4AFF] mx-auto mb-2" />
                <p className="text-[#6D4AFF] font-medium">Drop files here to upload</p>
              </div>
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className={`flex items-center justify-between px-3 py-2 mt-3 ${bgSecondary} rounded-lg border ${borderColor}`}>
          <p className={`text-sm ${textMuted}`}>
            {filteredFolders.length} folder(s), {filteredFiles.length} file(s)
          </p>
          <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] text-xs">
            {currentPath}
          </Badge>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor} max-w-3xl`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>{selectedFile?.name}</DialogTitle>
          </DialogHeader>
          {selectedFile && (
            <div className="space-y-4">
              <div className="max-h-[60vh] overflow-hidden rounded-lg bg-black/20 flex items-center justify-center">
                <img 
                  src={getFullUrl(selectedFile.url)} 
                  alt={selectedFile.name}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </div>
              <div className={`p-3 ${bgTertiary} rounded-lg`}>
                <p className={`text-xs ${textMuted} mb-1`}>Image URL</p>
                <div className="flex items-center gap-2">
                  <code className={`flex-1 text-sm ${textPrimary} break-all bg-black/20 p-2 rounded`}>{getFullUrl(selectedFile.url)}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyUrl(getFullUrl(selectedFile.url))}
                    className="flex-shrink-0"
                  >
                    {copiedUrl === getFullUrl(selectedFile.url) ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)} className={textMuted}>
              Close
            </Button>
            <Button 
              onClick={() => window.open(getFullUrl(selectedFile?.url), '_blank')}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor}`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className={`${bgTertiary} ${borderColor} ${textPrimary}`}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              data-testid="new-folder-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewFolderOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
              data-testid="create-folder-btn"
            >
              {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className={`${bgSecondary} ${borderColor}`}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>Rename File</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className={`text-sm ${textMuted} mb-2`}>Current name: {renameFile?.name}</p>
            <Input
              placeholder="New file name"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              className={`${bgTertiary} ${borderColor} ${textPrimary}`}
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
              data-testid="rename-input"
            />
            <p className={`text-xs ${textMuted} mt-2`}>
              The file extension will be preserved automatically.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)} className={textMuted}>
              Cancel
            </Button>
            <Button 
              onClick={handleRename}
              disabled={renaming || !newFileName.trim()}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
              data-testid="rename-btn"
            >
              {renaming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
